import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, collectionGroup, getDocs, query, where, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Header from '@/components/layout/Header';
import { 
  Users, 
  Calendar, 
  TrendingUp, 
  Settings, 
  Mail, 
  CreditCard, 
  ClipboardCheck, 
  Building2, 
  UserCog,
  Activity,
  AlertTriangle,
  BarChart3,
  UserMinus,
  Target,
  Clock,
  CheckCircle,
  XCircle,
  FileText,
  Gift,
  Package,
  Landmark,
} from 'lucide-react';
import { useAdminNotifications } from '@/hooks/useAdminNotifications';

interface Unit {
  id: string;
  name: string;
  number: string;
  members?: string[];
  createdAt: Date;
  createdBy: string;
  settings?: {
    timezone?: string;
    currency?: string;
  };
}

interface AdminStats {
  totalUnits: number;
  activeUnits: number;
  totalEvents: number;
  totalRevenue: number;
  monthlyRevenue: number;
  totalUsers: number;
  activeUsers: number;
  newUsersThisMonth: number;
  newUnitsThisMonth: number;
  newEventsThisMonth: number;
  userRetentionRate: number;
  unitRetentionRate: number;
  avgEventsPerUnit: number;
  avgUsersPerUnit: number;
  systemErrors: number;
  apiErrors: number;
  avgResponseTime: number;
  systemUptime: number;
  conversionRate: number;
  churnRate: number;
  monthlyGrowthRate: number;
  revenueGrowthRate: number;
}

export default function AdminPanelPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const adminNotifications = useAdminNotifications(true);
  const [stats, setStats] = useState<AdminStats>({
    totalUnits: 0,
    activeUnits: 0,
    totalEvents: 0,
    totalRevenue: 0,
    monthlyRevenue: 0,
    totalUsers: 0,
    activeUsers: 0,
    newUsersThisMonth: 0,
    newUnitsThisMonth: 0,
    newEventsThisMonth: 0,
    userRetentionRate: 0,
    unitRetentionRate: 0,
    avgEventsPerUnit: 0,
    avgUsersPerUnit: 0,
    systemErrors: 0,
    apiErrors: 0,
    avgResponseTime: 0,
    systemUptime: 99.9,
    conversionRate: 0,
    churnRate: 0,
    monthlyGrowthRate: 0,
    revenueGrowthRate: 0,
  });

  useEffect(() => {
    const fetchAdminStats = async () => {
      setLoading(true);
      const now = new Date();
      const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);

      const unitsList: Unit[] = [];
      let activeUnitsCount = 0;
      let newUnitsThisMonth = 0;
      let previousMonthNewUnits = 0;
      let totalEventsCount = 0;
      let newEventsThisMonth = 0;
      let totalUsers = 0;
      let activeUsers = 0;
      let newUsersThisMonth = 0;
      let prevCohortActiveUsers = 0;
      let totalRevenue = 0;
      let monthlyRevenue = 0;
      let lastMonthRevenue = 0;
      let systemErrors = 0;
      let apiErrors = 0;
      let apiSuccess = 0;
      let apiTotal = 0;
      let totalDurationMs = 0;

      console.log('📊 Fetching admin dashboard stats...');

      try {
        const unitsSnapshot = await getDocs(collection(db, 'units'));
        console.log(`✅ Fetched ${unitsSnapshot.size} units`);
        for (const unitDoc of unitsSnapshot.docs) {
          const data = unitDoc.data();
          const createdAt = data.createdAt?.toDate() || new Date();
          unitsList.push({
            id: unitDoc.id,
            name: data.name,
            number: data.unitNumber || data.number || '',
            members: data.members,
            createdAt,
            createdBy: data.createdBy || '',
            settings: data.settings || {},
          });
          const subscription = data.subscription || { status: 'trial', billingEmail: '' };
          if (subscription.status === 'active') activeUnitsCount++;
          if (createdAt >= thisMonth) newUnitsThisMonth++;
          if (createdAt >= lastMonth && createdAt < thisMonth) previousMonthNewUnits++;
        }
      } catch (err) {
        console.warn('⚠️ Could not fetch units:', err);
      }

      try {
        const allEventsSnapshot = await getDocs(collectionGroup(db, 'events'));
        console.log(`✅ Fetched ${allEventsSnapshot.size} events`);
        allEventsSnapshot.forEach((docSnap) => {
          const data = docSnap.data() as { date?: { toDate?: () => Date } | string };
          const rawDate = data.date;
          const eventDate = rawDate && typeof rawDate === 'object' && 'toDate' in rawDate
            ? (rawDate as { toDate: () => Date }).toDate()
            : rawDate ? new Date(rawDate as string) : undefined;
          totalEventsCount++;
          if (eventDate && eventDate >= thisMonth) newEventsThisMonth++;
        });
      } catch (err) {
        console.warn('⚠️ Could not fetch events (check collection group rule/index):', err);
      }

      try {
        const usersSnapshot = await getDocs(collection(db, 'users'));
        console.log(`✅ Fetched ${usersSnapshot.size} users`);
        const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        const sixtyDaysAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);
        usersSnapshot.forEach((userDoc) => {
          const data = userDoc.data();
          totalUsers++;
          const createdAt = data.createdAt?.toDate?.() || (data.createdAt ? new Date(data.createdAt as string) : undefined) || new Date(0);
          const lastLoginAt = data.lastLoginAt?.toDate?.() || (data.lastLoginAt ? new Date(data.lastLoginAt as string) : undefined);
          if (lastLoginAt && lastLoginAt >= thirtyDaysAgo) activeUsers++;
          if (createdAt && createdAt >= thisMonth) newUsersThisMonth++;
          if (lastLoginAt && lastLoginAt >= sixtyDaysAgo && lastLoginAt < thirtyDaysAgo) prevCohortActiveUsers++;
        });
      } catch (err) {
        console.warn('⚠️ Could not fetch users:', err);
      }

      try {
        const invoicesSnapshot = await getDocs(collectionGroup(db, 'invoices'));
        console.log(`✅ Fetched ${invoicesSnapshot.size} invoices`);
        invoicesSnapshot.forEach((invSnap) => {
          const inv = invSnap.data() as { amountPaid?: number; createdAt?: { toDate?: () => Date } | string };
          const amountPaid = typeof inv.amountPaid === 'number' ? inv.amountPaid : 0;
          totalRevenue += amountPaid;
          const createdAt = inv.createdAt && typeof inv.createdAt === 'object' && 'toDate' in inv.createdAt
            ? (inv.createdAt as { toDate: () => Date }).toDate()
            : inv.createdAt ? new Date(inv.createdAt as string) : undefined;
          if (createdAt && createdAt >= thisMonth) monthlyRevenue += amountPaid;
          if (createdAt && createdAt >= lastMonth && createdAt < thisMonth) lastMonthRevenue += amountPaid;
        });
      } catch (err) {
        console.warn('⚠️ Could not fetch invoices:', err);
      }

      const dayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      try {
        const dayAgoTimestamp = Timestamp.fromDate(dayAgo);
        const auditQuery = query(collection(db, 'audit_logs'), where('timestamp', '>=', dayAgoTimestamp));
        const auditSnapshot = await getDocs(auditQuery);
        console.log(`✅ Fetched ${auditSnapshot.size} audit logs from last 24h`);
        auditSnapshot.forEach((logSnap) => {
          const log = logSnap.data() as { type?: string; severity?: string; statusCode?: number; status?: string; duration?: number };
          if (log.type === 'error' || log.severity === 'error') systemErrors++;
          if (log.type === 'api_request') {
            apiTotal++;
            totalDurationMs += typeof log.duration === 'number' ? log.duration : 0;
            if (typeof log.statusCode === 'number' && log.statusCode >= 400) apiErrors++;
            if (log.status === 'success' || (typeof log.statusCode === 'number' && log.statusCode >= 200 && log.statusCode < 300)) apiSuccess++;
          }
        });
      } catch (auditError) {
        console.warn('⚠️ Could not fetch audit logs:', auditError);
      }

      const userRetentionRate = prevCohortActiveUsers > 0 ? (activeUsers / prevCohortActiveUsers) * 100 : 0;
      const unitRetentionRate = unitsList.length > 0 ? (activeUnitsCount / unitsList.length) * 100 : 0;
      const avgEventsPerUnit = unitsList.length > 0 ? totalEventsCount / unitsList.length : 0;
      const avgUsersPerUnit = unitsList.length > 0 ? totalUsers / unitsList.length : 0;
      const monthlyGrowthRate = previousMonthNewUnits > 0
        ? ((newUnitsThisMonth - previousMonthNewUnits) / previousMonthNewUnits) * 100
        : (newUnitsThisMonth > 0 ? 100 : 0);
      const revenueGrowthRate = lastMonthRevenue > 0
        ? ((monthlyRevenue - lastMonthRevenue) / lastMonthRevenue) * 100
        : (monthlyRevenue > 0 ? 100 : 0);
      const avgResponseTime = apiTotal > 0 ? Math.round(totalDurationMs / apiTotal) : 0;
      const systemUptime = apiTotal > 0 ? Math.round((apiSuccess / apiTotal) * 1000) / 10 : 99.9;
      const churnRate = Math.max(0, 100 - userRetentionRate);
      const conversionRate = unitsList.length > 0 ? (activeUnitsCount / unitsList.length) * 100 : 0;

      const newStats: AdminStats = {
        totalUnits: unitsList.length,
        activeUnits: activeUnitsCount,
        totalEvents: totalEventsCount,
        totalRevenue: Math.round(totalRevenue),
        monthlyRevenue: Math.round(monthlyRevenue),
        totalUsers,
        activeUsers,
        newUsersThisMonth,
        newUnitsThisMonth,
        newEventsThisMonth,
        userRetentionRate: Math.round(userRetentionRate * 10) / 10,
        unitRetentionRate: Math.round(unitRetentionRate * 10) / 10,
        avgEventsPerUnit: Math.round(avgEventsPerUnit * 10) / 10,
        avgUsersPerUnit: Math.round(avgUsersPerUnit * 10) / 10,
        systemErrors,
        apiErrors,
        avgResponseTime,
        systemUptime,
        conversionRate: Math.round(conversionRate * 10) / 10,
        churnRate: Math.round(churnRate * 10) / 10,
        monthlyGrowthRate: Math.round(monthlyGrowthRate * 10) / 10,
        revenueGrowthRate: Math.round(revenueGrowthRate * 10) / 10,
      };

      console.log('✅ Admin stats updated:', {
        units: newStats.totalUnits,
        users: newStats.totalUsers,
        events: newStats.totalEvents,
        revenue: `£${newStats.totalRevenue}`,
        monthlyRevenue: `£${newStats.monthlyRevenue}`,
        uptime: `${newStats.systemUptime}%`,
      });

      setStats(newStats);
      setLoading(false);
    };

    fetchAdminStats();
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
            <p className="text-muted-foreground">Loading admin data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="flex-1 px-4 py-8">
        <div className="container mx-auto">
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Platform Administration</h1>
              <p className="text-muted-foreground">Manage the Dining Steward platform</p>
            </div>
            <Button variant="outline" onClick={() => navigate('/settings')}>
              <Settings className="mr-2 h-4 w-4" />
              Platform Settings
            </Button>
          </div>

          {/* Key Metrics */}
          <div className="mb-8 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Units</CardTitle>
                <Building2 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.totalUnits}</div>
                <p className="text-xs text-muted-foreground">
                  {stats.activeUnits} active • {stats.newUnitsThisMonth} new this month
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Users</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.totalUsers}</div>
                <p className="text-xs text-muted-foreground">
                  {stats.activeUsers} active • {stats.newUsersThisMonth} new this month
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Events</CardTitle>
                <Calendar className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.totalEvents}</div>
                <p className="text-xs text-muted-foreground">
                  {stats.newEventsThisMonth} new this month • {stats.avgEventsPerUnit} avg per unit
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">System Uptime</CardTitle>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.systemUptime}%</div>
                <p className="text-xs text-green-600">Avg response: {stats.avgResponseTime}ms</p>
              </CardContent>
            </Card>
          </div>

          {/* Growth & Retention Analytics */}
          <div className="mb-8 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Growth Rate</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">+{stats.monthlyGrowthRate}%</div>
                <p className="text-xs text-muted-foreground">Monthly unit growth</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">User Retention</CardTitle>
                <Target className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.userRetentionRate}%</div>
                <p className="text-xs text-muted-foreground">30-day retention rate</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Conversion Rate</CardTitle>
                <CheckCircle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.conversionRate}%</div>
                <p className="text-xs text-muted-foreground">Trial to paid conversion</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Churn Rate</CardTitle>
                <UserMinus className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">{stats.churnRate}%</div>
                <p className="text-xs text-muted-foreground">Monthly user churn</p>
              </CardContent>
            </Card>
          </div>

          <div className="my-6 border-t" />

          {/* Revenue & Performance */}
          <div className="mb-8 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">£{stats.totalRevenue.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">All time revenue</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Monthly Revenue</CardTitle>
                <Calendar className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">£{stats.monthlyRevenue.toLocaleString()}</div>
                <p className="text-xs text-green-600">+{stats.revenueGrowthRate}% from last month</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Avg Revenue per Unit</CardTitle>
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">£{stats.totalUnits > 0 ? Math.round(stats.monthlyRevenue / stats.totalUnits) : 0}</div>
                <p className="text-xs text-muted-foreground">Monthly average</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">System Health</CardTitle>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{stats.systemUptime}%</div>
                <p className="text-xs text-muted-foreground">Uptime • {stats.avgResponseTime}ms avg</p>
              </CardContent>
            </Card>
          </div>

          {/* Error Monitoring */}
          <div className="mb-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">System Errors</CardTitle>
                <AlertTriangle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">{stats.systemErrors}</div>
                <p className="text-xs text-muted-foreground">Last 24 hours</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">API Errors</CardTitle>
                <XCircle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-600">{stats.apiErrors}</div>
                <p className="text-xs text-muted-foreground">Last 24 hours</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Response Time</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.avgResponseTime}ms</div>
                <p className="text-xs text-muted-foreground">Average API response</p>
              </CardContent>
            </Card>
          </div>


          {/* Quick Actions */}
          <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card 
              className="cursor-pointer transition-shadow hover:shadow-lg hover:border-primary"
              onClick={() => navigate('/user-management')}
            >
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <UserCog className="h-5 w-5" />
                  User Management
                </CardTitle>
                <CardDescription>View and manage platform users</CardDescription>
              </CardHeader>
            </Card>

            <Card 
              className="cursor-pointer transition-shadow hover:shadow-lg hover:border-primary"
              onClick={() => navigate('/unit-management')}
            >
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Unit Management
                </CardTitle>
                <CardDescription>View and manage all units</CardDescription>
              </CardHeader>
            </Card>

            <Card 
              className="cursor-pointer transition-shadow hover:shadow-lg hover:border-primary"
              onClick={() => navigate('/unit-reviews')}
            >
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ClipboardCheck className="h-5 w-5" />
                  Applications Under Review
                  {adminNotifications.pendingUnits > 0 && (
                    <Badge variant="destructive" className="ml-2">
                      {adminNotifications.pendingUnits}
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription>Review and approve pending unit applications</CardDescription>
              </CardHeader>
            </Card>

            <Card 
              className="cursor-pointer transition-shadow hover:shadow-lg hover:border-primary"
              onClick={() => navigate('/unit-types')}
            >
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Unit Types
                </CardTitle>
                <CardDescription>Manage masonic unit types</CardDescription>
              </CardHeader>
            </Card>

            <Card 
              className="cursor-pointer transition-shadow hover:shadow-lg hover:border-primary"
              onClick={() => navigate('/email-templates')}
            >
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  Email Templates
                </CardTitle>
                <CardDescription>Customize email notifications</CardDescription>
              </CardHeader>
            </Card>

            <Card 
              className="cursor-pointer transition-shadow hover:shadow-lg hover:border-primary"
              onClick={() => navigate('/invoice-management')}
            >
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Invoice Management
                </CardTitle>
                <CardDescription>View and manage all platform invoices</CardDescription>
              </CardHeader>
            </Card>

            <Card 
              className="cursor-pointer transition-shadow hover:shadow-lg hover:border-primary"
              onClick={() => navigate('/gift-credits-all-units')}
            >
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Gift className="h-5 w-5" />
                  Gift credits (all units)
                </CardTitle>
                <CardDescription>Bulk credit each unit with a full audit trail</CardDescription>
              </CardHeader>
            </Card>

            <Card 
              className="cursor-pointer transition-shadow hover:shadow-lg hover:border-primary"
              onClick={() => navigate('/credit-packs')}
            >
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Credit packs
                </CardTitle>
                <CardDescription>Edit purchasable bundles, prices, and descriptions</CardDescription>
              </CardHeader>
            </Card>

            <Card 
              className="cursor-pointer transition-shadow hover:shadow-lg hover:border-primary"
              onClick={() => navigate('/credits-reports')}
            >
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Credits & Sales Reports
                </CardTitle>
                <CardDescription>Download platform credit income reports</CardDescription>
              </CardHeader>
            </Card>

            <Card 
              className="cursor-pointer transition-shadow hover:shadow-lg hover:border-primary"
              onClick={() => navigate('/slickpay-orders')}
            >
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Landmark className="h-5 w-5" />
                  Open Banking Orders
                </CardTitle>
                <CardDescription>View and manage SlickPay PIS payment orders</CardDescription>
              </CardHeader>
            </Card>

            <Card 
              className="cursor-pointer transition-shadow hover:shadow-lg hover:border-primary"
              onClick={() => navigate('/event-management')}
            >
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Event Management
                </CardTitle>
                <CardDescription>View and manage all events across the platform</CardDescription>
              </CardHeader>
            </Card>

            <Card 
              className="cursor-pointer transition-shadow hover:shadow-lg hover:border-primary"
              onClick={() => navigate('/placecard-templates')}
            >
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Placecard Templates
                </CardTitle>
                <CardDescription>Create default place card templates</CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}

